import { useState, useCallback, useEffect } from "react";
import { TestTarget } from "../types/TestTarget";
import { fetchTestTargets as apiFetchTestTargets } from "../services/testTargetService";
import { SSTFApiClient } from "../services/api";

interface UseTestTargetsProps {
  apiClient?: SSTFApiClient | null;
  workspaceId?: string;
  projectId?: string;
  onAuthError?: () => void; // 401エラー時のコールバック
}

function isAuthError(err: unknown): boolean {
  return (
    err instanceof Error &&
    (err.message.includes("jwt expired") ||
      err.message.includes("トークンの検証に失敗") ||
      err.message.includes("401") ||
      err.message.includes("canActivate"))
  );
}

export const useTestTargets = (props?: UseTestTargetsProps) => {
  const [testTargets, setTestTargets] = useState<TestTarget[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTestTargets = useCallback(async (): Promise<
    TestTarget[] | undefined
  > => {
    if (!props?.apiClient || !props?.workspaceId || !props?.projectId) {
      setError("API設定が不完全です。設定を確認してください。");
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);

    let result: TestTarget[] | undefined;

    try {
      const data = await apiFetchTestTargets(
        props.apiClient,
        props.workspaceId,
        props.projectId
      );
      setTestTargets(data);
      result = data;
    } catch (err) {
      console.error("useTestTargets - error:", err);

      if (isAuthError(err)) {
        props?.onAuthError?.();
        setError("認証が無効です。設定画面でトークンを再設定してください。");
      } else {
        setError(
          err instanceof Error ? err.message : "不明なエラーが発生しました"
        );
      }
    } finally {
      setLoading(false);
    }

    return result;
  }, [props?.apiClient, props?.workspaceId, props?.projectId]);

  useEffect(() => {
    if (props?.apiClient && props?.workspaceId && props?.projectId) {
      void fetchTestTargets();
      return;
    }

    setLoading(false);
    if (props?.apiClient || props?.workspaceId || props?.projectId) {
      setError("API設定が不完全です。設定を確認してください。");
    } else {
      setError(null);
    }
  }, [props?.apiClient, props?.workspaceId, props?.projectId, fetchTestTargets]);

  const updateTestTarget = useCallback((updatedTarget: TestTarget) => {
    setTestTargets((currentTargets) =>
      currentTargets.map((target) =>
        target.id === updatedTarget.id ? updatedTarget : target
      )
    );
  }, []);

  const removeTestTarget = useCallback((targetId: string) => {
    setTestTargets((currentTargets) =>
      currentTargets.filter((target) => target.id !== targetId)
    );
  }, []);

  const addTestTarget = useCallback((newTarget: TestTarget) => {
    setTestTargets((currentTargets) => [...currentTargets, newTarget]);
    return Promise.resolve();
  }, []);

  return {
    testTargets,
    loading,
    error,
    fetchTestTargets,
    updateTestTarget,
    removeTestTarget,
    addTestTarget,
  };
};
